import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
} from '@mui/material';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';

export type MultiPatientConflictAction = 'discard' | 'switch' | 'dismiss';

interface MultiPatientConflictDialogProps {
    open: boolean;
    loadedPatientId: string;
    incomingPatientIds: string[];
    hasUnsavedChanges: boolean;
    clinicalUse: boolean;
    onAction: (action: MultiPatientConflictAction) => void;
}

/**
 * Dialog shown when the user attempts to load DICOM data for a
 * different patient while another patient is already loaded.
 *
 * In clinical mode only discard/switch are available. Outside of
 * clinical mode an additional "Load Both" dismiss button is shown.
 */
const MultiPatientConflictDialog: React.FC<MultiPatientConflictDialogProps> = ({
    open,
    loadedPatientId,
    incomingPatientIds,
    hasUnsavedChanges,
    clinicalUse,
    onAction,
}) => {
    const incomingPatientId = incomingPatientIds.join(', ');
    return (
        <Dialog
            open={open}
            onClose={() => onAction('discard')}
            aria-labelledby="multi-patient-conflict-dialog-title"
            aria-describedby="multi-patient-conflict-dialog-description"
            PaperProps={{ elevation: 0 }}
        >
            <DialogTitle id="multi-patient-conflict-dialog-title">
                <FormattedMessage id="patientConflict.title" />
            </DialogTitle>
            <DialogContent>
                <DialogContentText id="multi-patient-conflict-dialog-description">
                    <FormattedMessage
                        id={
                            clinicalUse
                                ? 'patientConflict.message'
                                : 'patientConflict.messageNonClinical'
                        }
                        values={{
                            loadedPatientId: (
                                <SensitiveInfoTypography>
                                    {loadedPatientId}
                                </SensitiveInfoTypography>
                            ),
                            incomingPatientId: (
                                <SensitiveInfoTypography>
                                    {incomingPatientId}
                                </SensitiveInfoTypography>
                            ),
                        }}
                    />
                </DialogContentText>
                {hasUnsavedChanges && (
                    <Alert
                        severity="warning"
                        sx={{ mt: 2 }}
                        data-testid="unsaved-changes-warning"
                    >
                        <FormattedMessage id="patientConflict.unsavedWarning" />
                    </Alert>
                )}
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={() => onAction('discard')}
                    color="primary"
                    data-testid="conflict-dialog-discard"
                >
                    <FormattedMessage id="patientConflict.discardIncoming" />
                </Button>
                {!clinicalUse && incomingPatientIds.length === 1 && (
                    <Button
                        onClick={() => onAction('dismiss')}
                        color="warning"
                        data-testid="conflict-dialog-dismiss"
                    >
                        <FormattedMessage id="patientConflict.dismiss" />
                    </Button>
                )}
                {incomingPatientIds.length === 1 && (
                    <Button
                        onClick={() => onAction('switch')}
                        color="error"
                        data-testid="conflict-dialog-switch"
                    >
                        <FormattedMessage id="patientConflict.switchPatient" />
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default MultiPatientConflictDialog;
